import React, { FC, PropsWithChildren } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styles from './sortable.component.module.scss';


interface SortableItemComponentProps extends PropsWithChildren {
    id: string;
    index: number;
    /** Replaces the default bootstrap row classes. See SortableComponent's `itemClassName`. */
    className?: string;
    /** Default true. False spreads the drag ACTIVATORS onto the item itself. */
    showHandle?: boolean;
}

const SortableItemComponent: FC<SortableItemComponentProps> = ({ id, index, className, showHandle = true, children }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: id });

    const style = {
        // TRANSLATE ONLY, never dnd-kit's scale. `DndContext` hands the active item a transform whose
        // `scaleX`/`scaleY` are `over.rect / active.rect` (core `adjustScale`), meant for a drag overlay
        // that should grow into the slot it hovers. Applied to the item itself it stretched a 4 s
        // Studio tile to 3.75x its width while it crossed a 15 s video (round 12, N2, O4: "the segment
        // being dragged gets really large and elongated"). Every shipped list here has equal-width
        // items, so the scale was always 1 for them: byte-identical output, one less way to be wrong.
        transform: CSS.Translate.toString(transform),
        transition: isDragging ? undefined : transition, // Disable transition during drag for instant response
        opacity: isDragging ? 0.4 : 1,
        // `touch-action: none` is what lets dnd-kit own the gesture -- and it is also why the
        // Studio never mounts this on a phone: an `overflow-x: auto` strip under it cannot be
        // finger-scrolled (STUDIO_DESIGN.md 6.7). The Studio's mobile track is a different render
        // branch, not a prop on this one.
        touchAction: 'none',
        zIndex: isDragging ? 999 : 'auto',
        position: 'relative' as 'relative',
    };

    /**
     * Handle-less: the whole item is the grip, so the POINTER activator goes on the item.
     *
     * Not `attributes`, and not the whole of `listeners`.
     *
     * `attributes` are `role="button" tabindex="0" aria-roledescription="sortable"
     * aria-describedby=…` -- correct on a 16 px grip that has no other meaning, wrong on a wrapper
     * whose child is already an interactive element. In the Studio that wrapper sits inside a
     * `role="listbox"`, where a `role="button"` child is not a valid option, and it would be a
     * second tab stop per clip competing with the roving tabindex STUDIO_DESIGN.md 6.8 specifies
     * (pre-flight row 35).
     *
     * `listeners` is built from EVERY sensor's activators (`useSyntheticListeners`), so it carries
     * the KeyboardSensor's `onKeyDown` as well as the PointerSensor's `onPointerDown`. Spreading
     * the whole object onto the item admitted a second, unasked-for keyboard drag mode: Space or
     * Enter on a clip (or on a trim handle, or on a transition marker) started a dnd-kit keyboard
     * drag, the arrows that followed were then handled twice -- once by dnd-kit and once by the
     * caller's own keydown -- and a closing Space committed a reorder nobody asked for. dnd-kit's
     * own guard against this is `setActivatorNodeRef` (`KeyboardSensor.activators` bails when
     * `event.target !== activator`), which this wrapper deliberately does not bind because the
     * item IS the activator for the pointer. So the keyboard activator is dropped here instead,
     * and the caller owns the keyboard reorder -- which is what the KDoc above always promised and
     * what the Studio's track actually implements (Alt+←/→ -> `onMove`).
     */
    const { onKeyDown: _dndKeyboardActivator, ...pointerActivators } = listeners ?? {};
    const itemProps = showHandle ? {} : pointerActivators;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`sortable-item ${styles["sortable-item"]} ${className ?? 'col-12 mb-2 g-0'}`}
            {...itemProps}
        >
            {showHandle &&
                <div
                    className={`d-flex align-items-center sort-handler ${styles['sort-handler']}`}
                    {...attributes}
                    {...listeners}
                >
                    <img width={"16px"} height={"16px"}
                        src="/icons/svg/drag-handler.svg"
                        alt='Drag'
                    />
                </div>
            }
            <div className='flex-grow-1'>
                {children}
            </div>
        </div>
    );
};

export default SortableItemComponent;
